<template>
  <div class="complex-card" :class="variant">
    <Card v-if="loading" class="w-full h-full max-w-352 animate-pulse" :class="cardClass" :elevation="elevation">
      <div
        :class="tallHeader ? 'h-[112px]' : 'h-80'"
        class="bg-grey-background-contrast b-skeleton b-skeleton-animate-wave mb-8"
      ></div>
      <div class="flex flex-row justify-between gap-8">
        <div class="w-3/4">
          <div class="bg-grey-background-contrast b-skeleton b-skeleton-animate-wave h-24 w-full mb-8"></div>
          <div class="bg-grey-background-contrast b-skeleton b-skeleton-animate-wave h-20 w-7/8 mb-8"></div>
          <div class="bg-grey-background-contrast b-skeleton b-skeleton-animate-wave h-20 w-full mb-8"></div>
        </div>
        <div class="w-1/4">
          <div
            class="bg-grey-background-contrast b-skeleton b-skeleton-animate-wave h-64 w-64 rounded-full outline outline-8 float-right"
            style="outline-color: white"
          ></div>
        </div>
      </div>
    </Card>
    <Card
      v-else
      :clickable="hasHref"
      class="w-full"
      :class="cardClass"
      :elevation="elevation"
      :aria-label="title"
      @click="viewDetails"
    >
      <div v-if="variant !== 'inactive'" :class="tallHeader ? 'h-[112px]' : 'h-80'">
        <img
          :src="headerImageUrl + photoSizeParam"
          class="complex-card-cover-img absolute rounded-t-xl left-0 top-0 object-cover overflow-hidden w-full"
          :class="tallHeader && 'tall-header'"
          alt=""
          loading="lazy"
        />
      </div>
      <div class="flex justify-between gap-8" :class="{ 'lg:h-100': isBase }">
        <div class="min-w-0">
          <h2
            class="complex-card-title text-strong-body mb-4 text-ellipsis overflow-hidden break-words"
            :class="titleClassComputed"
            style="-webkit-line-clamp: 2"
          >
            {{ title }}
          </h2>
          <p v-if="variant === 'base'" class="text-small text-grey-body mb-4 truncate">{{ firstLineText }}</p>
          <div class="truncate">
            <p v-if="variant === 'base'" class="text-small inline text-grey-body">
              {{ secondLineText }}
            </p>
            <p
              v-if="variant !== 'base'"
              class="text-small inline"
              :class="variant === 'inactive' ? 'text-grey-text-subdued' : 'text-grey-body'"
            >
              {{ firstLineText }}
            </p>
            <p
              v-if="(minPayRate || maxPayRate) && variant === 'active'"
              aria-hidden="true"
              class="text-grey-body text-small inline"
            >
              • ${{ payRateRange }}/hr
            </p>
          </div>
        </div>
        <div v-if="variant === 'base'" class="flex flex-col ml-auto pl-8">
          <Avatar
            :imageUrl="avatarImageUrl"
            :name="avatarName"
            size="xl"
            class="rounded-full border-solid border-2 border-foreground ml-auto -mt-40 z-[1]"
          />
          <p aria-hidden="true" class="flex whitespace-nowrap mt-auto pb-4">
            <span class="text-caption text-grey-text-subdued">$</span>
            <span class="text-h4">{{ payRateRange }}</span>
            <span class="text-grey-text-subdued">/hr</span>
          </p>
        </div>
      </div>
      <p v-if="variant === 'small'" class="text-small text-grey-body mt-4 truncate">
        {{ !secondLineText && !(minPayRate || maxPayRate) ? '\xa0' : secondLineText }}
        <span
          v-if="(minPayRate || maxPayRate) && variant === 'small'"
          aria-hidden="true"
          class="text-small text-grey-body mt-4 truncate inline"
        >
          ${{ payRateRange }}/hr
        </span>
      </p>
      <p v-if="minPayRate && maxPayRate" class="sr-only">{{ srOnlyPayRate }}</p>
    </Card>
  </div>
</template>

<script lang="ts">
import Card from '../card/Card.vue';
import Avatar from '../avatar/Avatar.vue';

export default {
  name: 'ComplexCard',
  components: { Card, Avatar },
  props: {
    variant: {
      type: String,
      required: true,
    },
    headerImageUrl: {
      default: '',
      type: String,
    },
    loading: {
      default: false,
      type: Boolean,
    },
    title: {
      default: '',
      type: String,
    },
    avatarFirstName: {
      default: '',
      type: String,
    },
    avatarLastInitial: {
      default: '',
      type: String,
    },
    avatarImageUrl: {
      default: '',
      type: String,
    },
    minPayRate: {
      default: null,
      type: Number,
    },
    maxPayRate: {
      default: null,
      type: Number,
    },
    detailsUrl: {
      default: '',
      required: false,
      type: String,
    },
    openInNewWindow: {
      type: Boolean,
      default: false,
      required: false,
    },
    firstLineText: {
      default: '',
      type: String,
    },
    secondLineText: {
      default: '',
      type: String,
    },
    tallHeader: {
      type: Boolean,
      default: false,
    },
    elevation: {
      type: Number,
      default: 2,
    },
    cardClass: {
      type: String,
      default: '',
    },
  },
  computed: {
    isBase(): boolean {
      return this.variant === 'base';
    },
    payRateTextComputed(): string {
      if (this.minPayRate && this.maxPayRate) {
        return `$${this.payRateRange}/hr`;
      }
      return '';
    },
    photoSizeParam(): string {
      return this.variant === 'small' ? '?w=212' : '?w=340';
    },
    titleClassComputed(): string {
      if (this.variant === 'inactive') return 'text-grey-text-subdued';
      if (this.variant === 'small') return 'text-grey-header h-48';
      return 'text-grey-header';
    },
    formattedMinRate(): string {
      return `${this.minPayRate / 100}`;
    },
    formattedMaxRate(): string {
      return `${this.maxPayRate / 100}`;
    },
    payRateRange(): string {
      return `${this.formattedMinRate}–${this.formattedMaxRate}`;
    },
    srOnlyPayRate(): string {
      return `${this.payRateRange} dollars per hour`;
    },
    avatarName(): string {
      return `${this.avatarFirstName} ${this.avatarLastInitial}`;
    },
    hasHref(): boolean {
      return !!this.detailsUrl;
    },
  },
  methods: {
    viewDetails() {
      if (this.detailsUrl) {
        this.$emit('ctaClicked');
        window.open(this.detailsUrl, this.openInNewWindow ? '_blank' : '_parent');
      }
    },
  },
};
</script>

<style scoped lang="scss">
.complex-card {
  &:not(.small, .active) {
    .card {
      max-width: 352px;
    }
  }
  &.small {
    flex: 1 1 0;
    width: 0;
    .card {
      height: 100%;
    }
  }
  .complex-card-cover-img {
    height: 88px;
    &.tall-header {
      height: 120px;
    }
  }
  .complex-card-title {
    display: -webkit-box;
    -webkit-box-orient: vertical;
  }
}
</style>
